/**
 * MergedConfigsTable
 * Single unified table for Chemicals, Caps, Bottles, Labels, Supplies.
 * Each row tagged with _type for per-type rendering and dialogs.
 * Columns: Type | Name | SKU | Details | Vendor | Cost | Actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Grid, Chip, IconButton, Tooltip,
  InputAdornment, alpha, useTheme, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Menu, MenuItem as MuiMenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
  Search as SearchIcon, Info as InfoIcon, KeyboardArrowDown as ArrowDownIcon, Close as CloseIcon
} from '@mui/icons-material';
import { supabase } from '../../../../../supabaseClient';
import ViewDialogue from '../../configuration-components/ViewDialogue';
import BottlesConfig from '../../configuration-components/BottlesConfig';
import CapsConfig from '../../configuration-components/CapsConfig';
import LabelsConfig from '../../configuration-components/LabelsConfig';
import SuppliesConfig from '../../configuration-components/SuppliesConfig';
import ChemicalsFormDialog from '../../configuration-components/chemicals-configs-components/ChemicalsFormDialog';
import ViewChemicalDialog from '../../configuration-components/chemicals-configs-components/ViewChemicalDialog';

// ── Type metadata ──────────────────────────────────────────────────────────────

const TYPE_INFO = {
  chemicals: { label: 'Chemical', table: 'raw_materials', singular: 'Chemical', color: '#7b1fa2', bg: '#f3e5f5' },
  caps:      { label: 'Cap',      table: 'caps',          singular: 'Cap',      color: '#00796b', bg: '#e0f2f1' },
  bottles:   { label: 'Bottle',   table: 'bottles',       singular: 'Bottle',   color: '#2e7d32', bg: '#e8f5e9' },
  labels:    { label: 'Label',    table: 'labels',        singular: 'Label',    color: '#e65100', bg: '#fff3e0' },
  supplies:  { label: 'Supply',   table: 'supplies',      singular: 'Supply',   color: '#5d4037', bg: '#efebe9' },
};

const ALL_TYPES = ['chemicals', 'caps', 'bottles', 'labels', 'supplies'];

const PLURAL = {
  chemicals: 'Chemicals',
  caps:      'Caps',
  bottles:   'Bottles',
  labels:    'Labels',
  supplies:  'Supplies',
};

// ── Details cell ──────────────────────────────────────────────────────────────

function DetailsCell({ item }) {
  if (item._type === 'chemicals') {
    const qty     = item.primary_unit_quantity;
    const measure = item.primary_unit_measure;
    const type    = item.primary_unit_type;
    if (qty && measure && type) {
      return (
        <Chip
          label={`${qty}${measure} per ${type}`}
          size="small" variant="outlined"
          sx={{ fontSize: '0.78rem', borderRadius: 2 }}
        />
      );
    }
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }
  return (
    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.9rem' }}>
      {item.material || '-'}
    </Typography>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function MergedConfigsTable({ companyId, suppliers, canEdit = true, canDelete = true }) {
  const theme = useTheme();

  const [items,          setItems]          = useState([]);
  const [filtered,       setFiltered]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [searchQuery,    setSearchQuery]    = useState('');
  const [typeFilter,     setTypeFilter]     = useState('all');
  const [vendorFilter,   setVendorFilter]   = useState('');

  // Vendor dialog state
  const [vendorDialogOpen,     setVendorDialogOpen]     = useState(false);
  const [vendorSearchTerm,     setVendorSearchTerm]     = useState('');
  const [vendorDialogSelected, setVendorDialogSelected] = useState(null);

  const [page,           setPage]           = useState(0);
  const [rowsPerPage,    setRowsPerPage]    = useState(25);

  // Add / Edit dialog state
  const [addMenuAnchor,  setAddMenuAnchor]  = useState(null);
  const [dialogType,     setDialogType]     = useState(null);
  const [dialogOpen,     setDialogOpen]     = useState(false);
  const [editingItem,    setEditingItem]    = useState(null);

  // View dialog state
  const [viewOpen,       setViewOpen]       = useState(false);
  const [viewItem,       setViewItem]       = useState(null);

  // Delete dialog state
  const [deleteOpen,     setDeleteOpen]     = useState(false);
  const [itemToDelete,   setItemToDelete]   = useState(null);
  const [deleting,       setDeleting]       = useState(false);

  const fetchAll = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const [chem, caps, bottles, labels, supplies] = await Promise.all([
        supabase.from('raw_materials').select('*, supplier:supplier_id(id, name)').eq('company_id', companyId).order('name'),
        supabase.from('caps').select('*, supplier:supplier_id(id, name)').eq('company_id', companyId).order('created_at', { ascending: false }),
        supabase.from('bottles').select('*, supplier:supplier_id(id, name)').eq('company_id', companyId).order('created_at', { ascending: false }),
        supabase.from('labels').select('*, supplier:supplier_id(id, name)').eq('company_id', companyId).order('created_at', { ascending: false }),
        supabase.from('supplies').select('*, supplier:supplier_id(id, name)').eq('company_id', companyId).order('created_at', { ascending: false }),
      ]);
      const merged = [
        ...(chem.data    || []).map(i => ({ ...i, _type: 'chemicals' })),
        ...(caps.data    || []).map(i => ({ ...i, _type: 'caps'      })),
        ...(bottles.data || []).map(i => ({ ...i, _type: 'bottles'   })),
        ...(labels.data  || []).map(i => ({ ...i, _type: 'labels'    })),
        ...(supplies.data|| []).map(i => ({ ...i, _type: 'supplies'  })),
      ].sort((a, b) => {
        const order = ALL_TYPES.indexOf(a._type) - ALL_TYPES.indexOf(b._type);
        if (order !== 0) return order;
        return (a.name || '').localeCompare(b.name || '');
      });
      setItems(merged);
    } catch (err) { console.error('MergedConfigsTable fetch error:', err); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    let result = items;
    if (typeFilter !== 'all') result = result.filter(i => i._type === typeFilter);
    if (vendorFilter) result = result.filter(i => i.supplier_id === vendorFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i =>
        i.name?.toLowerCase().includes(q) ||
        i.sku?.toLowerCase().includes(q) ||
        i.material?.toLowerCase().includes(q) ||
        TYPE_INFO[i._type]?.label.toLowerCase().includes(q)
      );
    }
    setFiltered(result);
    setPage(0);
  }, [items, typeFilter, vendorFilter, searchQuery]);

  const openAdd = (type) => {
    setEditingItem(null);
    setDialogType(type);
    setDialogOpen(true);
    setAddMenuAnchor(null);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setDialogType(item._type);
    setDialogOpen(true);
  };

  const openView = (item) => {
    setViewItem(item);
    setViewOpen(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    const { _type, id } = itemToDelete;
    setDeleting(true);
    try {
      if (_type === 'chemicals') {
        await supabase.from('formulation_ingredients_new').delete().eq('ingredient_id', id);
        await supabase.from('batch_ingredients').delete().eq('raw_material_id', id);
        await supabase.from('product_raw_material_associations').delete().eq('raw_material_id', id);
        await supabase.from('raw_materials_inventory_items').delete().eq('raw_material_id', id);
        await supabase.from('raw_materials_transactions').delete().eq('raw_material_id', id);
        await supabase.from('raw_materials_inventory').delete().eq('raw_material_id', id);
        await supabase.from('raw_materials').delete().eq('id', id);
      } else {
        const idCol = `${_type.slice(0, -1)}_id`;
        await Promise.all([
          supabase.from(`${_type}_inventory`).delete().eq(idCol, id),
          supabase.from(`${_type}_transactions`).delete().eq(idCol, id),
          supabase.from(`product_${_type}`).delete().eq(idCol, id),
        ]);
        await supabase.from(TYPE_INFO[_type].table).delete().eq('id', id);
      }
      setDeleteOpen(false);
      setItemToDelete(null);
      fetchAll();
    } catch (err) { console.error(err); }
    finally { setDeleting(false); }
  };

  const closeDialog = () => { setDialogOpen(false); setEditingItem(null); fetchAll(); };

  const paginated   = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const hCellSx     = {
    fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    color: 'primary.main', py: 2, px: 2.5, whiteSpace: 'nowrap'
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        mb: 3, p: 3, borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        transition: 'all 0.3s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}` }
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 500, fontSize: '1.8rem', mb: 1 }}>
            All Item Configurations
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem', mb: 2.5, maxWidth: 480 }}>
            Manage chemicals, caps, bottles, labels, and supplies catalogs in one view
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip label={`${items.length} Total Items`} size="small"
              sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }} />
            {ALL_TYPES.map(t => {
              const count = items.filter(i => i._type === t).length;
              if (!count) return null;
              const info = TYPE_INFO[t];
              return (
                <Chip key={t} label={`${count} ${count !== 1 ? PLURAL[t] : info.singular}`} size="small"
                  sx={{ bgcolor: info.bg, color: info.color, fontWeight: 500, border: `1px solid ${info.color}44` }} />
              );
            })}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAll}
              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, color: 'primary.main' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} endIcon={<ArrowDownIcon />}
            disabled={!canEdit}
            onClick={(e) => setAddMenuAnchor(e.currentTarget)}
            sx={{ borderRadius: 2, px: 3, py: 1.2, fontSize: '0.9rem', fontWeight: 500, whiteSpace: 'nowrap',
              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`, '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500', boxShadow: 'none' } }}>
            Add Item
          </Button>
          <Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={() => setAddMenuAnchor(null)}>
            {ALL_TYPES.map(t => (
              <MuiMenuItem key={t} onClick={() => openAdd(t)} sx={{ gap: 1.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: TYPE_INFO[t].color, flexShrink: 0 }} />
                Add {TYPE_INFO[t].singular}
              </MuiMenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      {/* Search + Vendor + Type Filter — single row */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.1)}` }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>

          {/* Search */}
          <TextField placeholder="Search by name, SKU, material, or type…" value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'primary.main' }} /></InputAdornment>,
              sx: { borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white', height: 44,
                '&:hover': { border: `1px solid ${theme.palette.primary.main}` } }
            }}
            sx={{ width: 600, '& .MuiOutlinedInput-root': { height: 44, '& fieldset': { border: 'none' } } }}
          />

          {/* Vendor filter button */}
          <Button variant="outlined" endIcon={<ArrowDownIcon />}
            onClick={() => {
              setVendorDialogOpen(true);
              setVendorSearchTerm('');
              setVendorDialogSelected(vendorFilter ? (suppliers || []).find(s => s.id === vendorFilter) || null : null);
            }}
            sx={{
              height: 44, borderRadius: 3, textTransform: 'none', whiteSpace: 'nowrap',
              minWidth: 420, justifyContent: 'space-between', fontWeight: 500,
              bgcolor: vendorFilter ? alpha(theme.palette.primary.main, 0.08) : 'white',
              border: vendorFilter ? `1px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              color: vendorFilter ? 'primary.main' : 'text.secondary',
              '&:hover': { border: `1px solid ${theme.palette.primary.main}`, bgcolor: alpha(theme.palette.primary.main, 0.04) }
            }}>
            {vendorFilter ? (suppliers || []).find(s => s.id === vendorFilter)?.name || 'Vendor' : 'All Vendors'}
          </Button>

          <Divider orientation="vertical" flexItem sx={{ borderColor: alpha(theme.palette.grey[400], 0.5) }} />

          {/* Type filter chips */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>Filter:</Typography>
            {[{ key: 'all', label: 'All' }, ...ALL_TYPES.map(t => ({ key: t, label: PLURAL[t] }))].map(({ key, label }) => (
              <Chip key={key} label={label} size="small" clickable
                onClick={() => setTypeFilter(key)}
                sx={{
                  fontWeight: 500,
                  bgcolor: typeFilter === key ? (key === 'all' ? theme.palette.primary.main : TYPE_INFO[key]?.color) : 'white',
                  color: typeFilter === key ? 'white' : (key === 'all' ? 'text.primary' : TYPE_INFO[key]?.color),
                  border: `1px solid ${key === 'all' ? alpha(theme.palette.primary.main, 0.5) : (TYPE_INFO[key]?.color + '66')}`,
                }}
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Merged Table */}
      <Paper sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`, overflow: 'hidden' }}>
        <TableContainer sx={{ overflow: 'hidden' }}>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...hCellSx, width: '11%' }}>Type</TableCell>
                <TableCell sx={{ ...hCellSx, width: '22%' }}>Name</TableCell>
                <TableCell sx={{ ...hCellSx, width: '13%' }}>SKU</TableCell>
                <TableCell sx={{ ...hCellSx, width: '18%' }}>Details</TableCell>
                <TableCell sx={{ ...hCellSx, width: '18%' }}>Vendor</TableCell>
                <TableCell sx={{ ...hCellSx, width: '10%' }}>Cost</TableCell>
                <TableCell align="center" sx={{ ...hCellSx, width: '8%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((item, idx) => {
                const info = TYPE_INFO[item._type];
                return (
                  <TableRow key={`${item._type}-${item.id}`} hover onClick={() => openView(item)}
                    sx={{ cursor: 'pointer', transition: 'all 0.2s ease',
                      bgcolor: idx % 2 === 0 ? 'white' : alpha(theme.palette.grey[50], 0.8),
                      '&:hover': { bgcolor: 'grey.50', transform: 'translateX(4px)', boxShadow: 'inset 4px 0 0 black' },
                      '& .MuiTableCell-root': { borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`, py: 2.5, px: 2.5 } }}>
                    <TableCell>
                      <Chip label={info.label} size="small"
                        sx={{ bgcolor: info.bg, color: info.color, fontWeight: 500,
                          border: `1px solid ${info.color}55`, fontSize: '0.75rem' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem', color: 'text.primary' }}>
                        {item.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.9rem' }}>
                        {item.sku || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell><DetailsCell item={item} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        {item.supplier?.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        {item.unit_cost ? `$${parseFloat(item.unit_cost).toFixed(2)}` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton size="small" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); openEdit(item); }}
                          sx={{ color: 'primary.main', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: 1.5,
                            '&:hover': { bgcolor: 'primary.main', color: 'white' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" disabled={!canDelete} onClick={(e) => { e.stopPropagation(); handleDeleteClick(item); }}
                          sx={{ color: 'error.main', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: 1.5,
                            '&:hover': { bgcolor: 'error.main', color: 'white' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">No items found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
          <TablePagination component="div" count={filtered.length} page={page}
            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]} />
        </Paper>
      </Box>

      {/* Add / Edit Dialogs */}
      {dialogType === 'chemicals' && (
        <ChemicalsFormDialog open={dialogOpen} onClose={closeDialog}
          editingItem={editingItem} companyId={companyId} suppliers={suppliers || []} />
      )}
      {dialogType === 'caps' && (
        <CapsConfig companyId={companyId} suppliers={suppliers || []} open={dialogOpen}
          onClose={closeDialog} editingItem={editingItem} />
      )}
      {dialogType === 'bottles' && (
        <BottlesConfig companyId={companyId} suppliers={suppliers || []} open={dialogOpen}
          onClose={closeDialog} editingItem={editingItem} />
      )}
      {dialogType === 'labels' && (
        <LabelsConfig companyId={companyId} suppliers={suppliers || []} open={dialogOpen}
          onClose={closeDialog} editingItem={editingItem} />
      )}
      {dialogType === 'supplies' && (
        <SuppliesConfig companyId={companyId} suppliers={suppliers || []} open={dialogOpen}
          onClose={closeDialog} editingItem={editingItem} />
      )}

      {/* View Dialogs */}
      {viewItem?._type === 'chemicals' ? (
        <ViewChemicalDialog open={viewOpen} onClose={() => { setViewOpen(false); setViewItem(null); }}
          item={viewItem} onEdit={(item) => { setViewOpen(false); openEdit(item); }} />
      ) : (
        <ViewDialogue open={viewOpen && viewItem?._type !== 'chemicals'}
          onClose={() => { setViewOpen(false); setViewItem(null); }}
          item={viewItem} itemType={viewItem?._type}
          onEdit={(item) => { setViewOpen(false); openEdit(item); }} />
      )}

      {/* Vendor Filter Dialog */}
      <Dialog open={vendorDialogOpen} onClose={() => setVendorDialogOpen(false)} fullWidth maxWidth="xl"
        PaperProps={{ sx: { borderRadius: 4, height: '80vh', maxHeight: '80vh', margin: 'auto',
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, boxShadow: '0 24px 48px rgba(0,0,0,0.15)' } }}>
        <DialogTitle sx={{ pb: 2, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>Select Vendor</Typography>
          <IconButton onClick={() => setVendorDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField fullWidth placeholder="Search vendors..." value={vendorSearchTerm}
              onChange={(e) => setVendorSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: theme.palette.primary.main }} /></InputAdornment>,
                sx: { borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white',
                  '&:hover': { border: `1px solid ${theme.palette.primary.main}`, boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}` } } }}
              sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' }, '&:hover fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } } }} />
          </Box>
          <Box sx={{ overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Vendor</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(suppliers || []).filter(s => !vendorSearchTerm || s.name.toLowerCase().includes(vendorSearchTerm.toLowerCase())).map(s => (
                  <TableRow key={s.id} hover sx={{ cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.50', transform: 'translateX(4px)', boxShadow: 'inset 4px 0 0 black' },
                    '& .MuiTableCell-root': { borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`, py: 2, pl: 3, pr: 3 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>{s.name}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button size="small" variant="outlined" onClick={() => setVendorDialogSelected(s)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, fontSize: '0.8rem', px: 2, py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          bgcolor: vendorDialogSelected?.id === s.id ? 'black' : 'white',
                          color: vendorDialogSelected?.id === s.id ? 'white' : 'text.primary',
                          '&:hover': { border: `1px solid ${theme.palette.primary.main}`, transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                            bgcolor: vendorDialogSelected?.id === s.id ? 'black' : 'white',
                            color: vendorDialogSelected?.id === s.id ? 'white' : 'text.primary' } }}>
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, background: alpha(theme.palette.grey[50], 0.5) }}>
          <Button onClick={() => { setVendorFilter(''); setVendorDialogOpen(false); }} sx={{ borderRadius: 2, px: 3 }}>Clear Filter</Button>
          <Button onClick={() => setVendorDialogOpen(false)} sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
          <Button variant="contained" onClick={() => { setVendorFilter(vendorDialogSelected?.id || ''); setVendorDialogOpen(false); }}
            sx={{ borderRadius: 2, px: 3, bgcolor: 'black', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DeleteIcon color="error" />Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete <strong>"{itemToDelete?.name}"</strong>?
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            All related inventory records and transactions will also be deleted. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting} sx={{ borderRadius: 2 }}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
